import { MedicationPaymentHistory } from "../../types";

// Type definitions (corrected structure)
export interface ActiveMedication {
    name: string;
    dosage: string;
    quantity: number;
    duration: string;
    cost: number;
    medication_id: string;
}

export interface ProcessingRefill {
    created_at: number;
    delivery_date: string;
    delivery_status: string;
    medication: ActiveMedication;
    patient_id: string;
    payment_status: string;
    prescription_id: string;
    refill_id: string;
    refill_status: string;
    request_date: string;
    request_datetime: string;
}

export interface ProcessingMedication {
    created_at: number;
    delivery_date: string;
    delivery_status: string;
    medication: ActiveMedication;
    patient_id: string;
    payment_status: string;
    prescription_id: string;
    refill_id: string;
    refill_status: string;
    request_date: string;
    request_datetime: string;
}

export type CurrentProcessing = ProcessingRefill & {
    name: string;
    delivery_date: string;
    request_date: string;
    progress: {
        status: string;
        label: string;
        date: string;
    }[];
    created_at?: number;
};

export type GetCurrentProcessingResponse = {
    processing_medications: {
        processing_refills: ProcessingRefill[];
        processing_medications: ProcessingMedication[];
    }
};

// Helper function to generate timestamps
const getTimestamp = (daysAgo: number): number => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return Math.floor(date.getTime() / 1000);
};

// Helper function to get date string
const getDateString = (daysFromNow: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString(); // YYYY-MM-DD format
    // return date.toISOString().split('T')[0] || ""; // YYYY-MM-DD format
};

// Helper function to get datetime string
const getDateTimeString = (daysFromNow: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString(); // Full ISO datetime
};

// Mock data
export const mockGetCurrentProcessingResponse: GetCurrentProcessingResponse = {
    processing_medications: {
        processing_refills: [
            {
                created_at: getTimestamp(3),
                delivery_date: getDateString(2),
                delivery_status: "processing",
                medication: {
                    name: "Lisinopril",
                    dosage: "10mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 25.50,
                    medication_id: "med_001"
                },
                patient_id: "patient_123",
                payment_status: "paid",
                prescription_id: "rx_001",
                refill_id: "refill_001",
                refill_status: "approved",
                request_date: getDateString(-3),
                request_datetime: getDateTimeString(-3)
            },
            {
                created_at: getTimestamp(5),
                delivery_date: getDateString(1),
                delivery_status: "shipped",
                medication: {
                    name: "Metformin",
                    dosage: "500mg",
                    quantity: 60,
                    duration: "30 days",
                    cost: 15.75,
                    medication_id: "med_002"
                },
                patient_id: "patient_123",
                payment_status: "paid",
                prescription_id: "rx_002",
                refill_id: "refill_002",
                refill_status: "processing",
                request_date: getDateString(-5),
                request_datetime: getDateTimeString(-5)
            },
            {
                created_at: getTimestamp(1),
                delivery_date: getDateString(3),
                delivery_status: "pending",
                medication: {
                    name: "Atorvastatin",
                    dosage: "20mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 32.00,
                    medication_id: "med_003"
                },
                patient_id: "patient_123",
                payment_status: "pending",
                prescription_id: "rx_003",
                refill_id: "refill_003",
                refill_status: "requested",
                request_date: getDateString(-1),
                request_datetime: getDateTimeString(-1)
            },
            {
                created_at: getTimestamp(7),
                delivery_date: getDateString(0),
                delivery_status: "out_for_delivery",
                medication: {
                    name: "Losartan",
                    dosage: "50mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 18.25,
                    medication_id: "med_004"
                },
                patient_id: "patient_123",
                payment_status: "paid",
                prescription_id: "rx_004",
                refill_id: "refill_004",
                refill_status: "approved",
                request_date: getDateString(-7),
                request_datetime: getDateTimeString(-7)
            }
        ],
        processing_medications: [
            {
                created_at: getTimestamp(2),
                delivery_date: getDateString(4),
                delivery_status: "processing",
                medication: {
                    name: "Sertraline",
                    dosage: "50mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 28.90,
                    medication_id: "med_005"
                },
                patient_id: "patient_123",
                payment_status: "paid",
                prescription_id: "rx_005",
                refill_id: "refill_005",
                refill_status: "processing",
                request_date: getDateString(-2),
                request_datetime: getDateTimeString(-2)
            },
            {
                created_at: getTimestamp(4),
                delivery_date: getDateString(5),
                delivery_status: "pending",
                medication: {
                    name: "Omeprazole",
                    dosage: "20mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 12.45,
                    medication_id: "med_006"
                },
                patient_id: "patient_123",
                payment_status: "pending",
                prescription_id: "rx_006",
                refill_id: "refill_006",
                refill_status: "requested",
                request_date: getDateString(-4),
                request_datetime: getDateTimeString(-4)
            },
            {
                created_at: getTimestamp(6),
                delivery_date: getDateString(1),
                delivery_status: "shipped",
                medication: {
                    name: "Amlodipine",
                    dosage: "5mg",
                    quantity: 30,
                    duration: "30 days",
                    cost: 22.80,
                    medication_id: "med_007"
                },
                patient_id: "patient_123",
                payment_status: "paid",
                prescription_id: "rx_007",
                refill_id: "refill_007",
                refill_status: "approved",
                request_date: getDateString(-6),
                request_datetime: getDateTimeString(-6)
            }
        ]
    }
};


// Example usage with your transformation logic:
/*
// Assuming you have the format functions available
const formatRefillDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};
 
const formatRefillDateAbbreviated = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
};
 
// Your transformation logic
const data = mockGetCurrentProcessingResponse;
const processingRefills = data?.processing_medications?.processing_refills;
const processingMedications = data?.processing_medications?.processing_medications;
 
console.log("processingRefills", processingRefills);
console.log("processingMedications", processingMedications);
 
const transformedRefills = processingRefills?.map((refill) => ({
  ...refill,
  name: refill.medication.name,
  delivery_date: formatRefillDate(refill.delivery_date),
  request_date: formatRefillDate(refill.request_date),
  progress: [
    {
      status: 'completed',
      label: 'Requested',
      date: formatRefillDateAbbreviated(refill.request_date)
    },
    {
      status: 'completed',
      label: 'Approved',
      date: formatRefillDateAbbreviated(refill.request_date)
    },
    {
      status: 'pending',
      label: 'Order Placed',
      date: formatRefillDateAbbreviated(refill.request_date)
    },
    {
      status: 'pending',
      label: 'Delivered',
      date: formatRefillDateAbbreviated(refill.delivery_date)
    },
  ]
}));
 
console.log("Transformed refills with progress:", transformedRefills);
*/

// Mock data for GetMedicalHistoryResponse
export const mockMedicalHistoryResponse = {
    // Assuming base Response fields
    success: true,
    message: "Medical history retrieved successfully",
    status_code: 200,

    refill_history: [
        {
            refill_id: "rf_001_2024_12_15",
            encounter_id: "enc_789456123",
            prescription_id: "rx_456789012",
            medication_id: "med_123456789",
            patient_id: "pat_987654321",
            refill_status: "rejected",
            medication: {
                medication_id: "med_123456789",
                name: "Lisinopril",
                dosage: "10mg",
                duration: "30 days",
                frequency: "Once daily",
                quantity: 30,
                medication_cost: 25.99,
                cost: 25.99
            },
            payment_status: "paid",
            request_date: "2024-12-15T09:30:00Z",
            delivery_date: "2024-12-17T14:20:00Z",
            delivery_status: "delivered",
            reason: "Side effects too severe",
            created_at: 1734249000000
        },
        {
            refill_id: "rf_002_2024_12_10",
            encounter_id: "enc_789456124",
            prescription_id: "rx_456789013",
            medication_id: "med_123456790",
            patient_id: "pat_987654321",
            refill_status: "completed",
            medication: {
                medication_id: "med_123456790",
                name: "Metformin",
                dosage: "500mg",
                duration: "90 days",
                frequency: "Twice daily",
                quantity: 180,
                medication_cost: 45.50,
                cost: 45.50
            },
            payment_status: "paid",
            request_date: "2024-12-10T11:15:00Z",
            delivery_date: "2024-12-12T16:45:00Z",
            delivery_status: "shipped",
            reason: "Side effects too severe",
            created_at: 1733817300000
        },
        {
            refill_id: "rf_003_2024_12_08",
            encounter_id: "enc_789456125",
            prescription_id: "rx_456789014",
            medication_id: "med_123456791",
            patient_id: "pat_987654321",
            refill_status: "rejected",
            medication: {
                medication_id: "med_123456791",
                name: "Amlodipine",
                dosage: "5mg",
                duration: "30 days",
                frequency: "Once daily",
                quantity: 30,
                medication_cost: 18.75,
                cost: 18.75
            },
            payment_status: "not_paid",
            request_date: "2024-12-08T13:22:00Z",
            delivery_date: "2024-12-12T16:45:00Z",
            delivery_status: "pending",
            reason: "Side effects too severe",
            created_at: 1733644920000
        },
        {
            refill_id: "rf_004_2024_11_28",
            encounter_id: "enc_789456126",
            prescription_id: "rx_456789015",
            medication_id: "med_123456792",
            patient_id: "pat_987654321",
            refill_status: "completed",
            medication: {
                medication_id: "med_123456792",
                name: "Atorvastatin",
                dosage: "20mg",
                duration: "30 days",
                frequency: "Once daily at bedtime",
                quantity: 30,
                medication_cost: 32.00,
                cost: 32.00
            },
            payment_status: "not_paid",
            request_date: "2024-11-28T10:05:00Z",
            delivery_date: "2024-12-12T16:45:00Z",
            delivery_status: "pending",
            reason: "Side effects too severe",
            created_at: 1732784700000
        },
        {
            refill_id: "rf_005_2024_11_20",
            encounter_id: "enc_789456127",
            prescription_id: "rx_456789016",
            medication_id: "med_123456793",
            patient_id: "pat_987654321",
            refill_status: "completed",
            medication: {
                medication_id: "med_123456793",
                name: "Omeprazole",
                dosage: "20mg",
                duration: "60 days",
                frequency: "Once daily before breakfast",
                quantity: 60,
                medication_cost: 28.95,
                cost: 28.95
            },
            payment_status: "paid",
            request_date: "2024-11-20T08:45:00Z",
            delivery_date: "2024-11-22T12:30:00Z",
            delivery_status: "delivered",
            reason: "Side effects too severe",
            created_at: 1732093500000
        }
    ]
};

// Additional mock data examples for different scenarios
export const mockDataVariations = {
    // Example with custom refill_status
    customStatus: {
        refill_id: "rf_006_2024_10_15",
        encounter_id: "enc_789456128",
        prescription_id: "rx_456789017",
        medication_id: "med_123456794",
        patient_id: "pat_987654321",
        refill_status: "partially_filled", // custom status
        medication: {
            medication_id: "med_123456794",
            name: "Gabapentin",
            dosage: "300mg",
            duration: "30 days",
            frequency: "Three times daily",
            quantity: 90,
            medication_cost: 42.30,
            cost: 42.30
        },
        payment_status: "pending", // custom payment status
        request_date: "2024-10-15T14:20:00Z",
        delivery_date: "2024-10-17T09:15:00Z",
        delivery_status: "delivered",
        created_at: 1729001200000
    },

    // Example with different medication types
    inhalerMedication: {
        refill_id: "rf_007_2024_10_10",
        encounter_id: "enc_789456129",
        prescription_id: "rx_456789018",
        medication_id: "med_123456795",
        patient_id: "pat_987654321",
        refill_status: "completed",
        medication: {
            medication_id: "med_123456795",
            name: "Albuterol HFA Inhaler",
            dosage: "90mcg per actuation",
            duration: "30 days",
            frequency: "2 puffs every 4-6 hours as needed",
            quantity: 1,
            medication_cost: 65.00,
            cost: 65.00
        },
        payment_status: "paid",
        request_date: "2024-10-10T16:30:00Z",
        delivery_date: "2024-10-12T10:45:00Z",
        delivery_status: "delivered",
        created_at: 1728569400000
    }
};


// Mock data for GetMedicalPaymentHistoryResponse
const mockMedicalPaymentHistoryResponse = {
    success: true,
    message: "Payment history retrieved successfully",
    status_code: 200,
    payment_history: [
      {
        id: "pay_001_2024_12_17",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-12-17T14:30:00Z",
        amount: 25.99
      },
      {
        id: "pay_002_2024_12_12",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-12-12T16:45:00Z",
        amount: 45.50
      },
      {
        id: "pay_003_2024_12_10",
        type: "Weight Loss Program Consultation",
        status: "failed",
        date: "2024-12-10T09:15:00Z",
        amount: 15.00
      },
      {
        id: "pay_004_2024_11_28",
        type: "Weight Loss Program Consultation",
        status: "failed",
        date: "2024-11-28T11:20:00Z",
        amount: 32.00
      },
      {
        id: "pay_005_2024_11_22",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-11-22T13:45:00Z",
        amount: 28.95
      },
      {
        id: "pay_006_2024_11_15",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-11-15T10:30:00Z",
        amount: 150.00
      },
      {
        id: "pay_007_2024_10_25",
        type: "Weight Loss Program Consultation",
        status: "failed",
        date: "2024-10-25T16:20:00Z",
        amount: 42.30
      },
      {
        id: "pay_008_2024_10_18",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-10-18T08:15:00Z",
        amount: 385.75
      },
      {
        id: "pay_009_2024_10_12",
        type: "Weight Loss Program Consultation",
        status: "paid",
        date: "2024-10-12T14:10:00Z",
        amount: 20.00
      },
      {
        id: "pay_010_2024_09_30",
        type: "Weight Loss Program Consultation",
        status: "failed",
        date: "2024-09-30T12:05:00Z",
        amount: 18.75
      }
    ]
  };
  
  // Additional mock data examples for different scenarios
  const mockPaymentDataVariations = {
    // High-value specialty medication payment
    specialtyPayment: {
      id: "pay_sp_001_2024_12_01",
      type: "specialty_biologic",
      status: "paid",
      date: "2024-12-01T09:00:00Z",
      amount: 1250.00
    },
  
    // Insurance adjustment/credit
    insuranceCredit: {
      id: "pay_ins_002_2024_11_10",
      type: "insurance_adjustment",
      status: "paid",
      date: "2024-11-10T15:30:00Z",
      amount: -75.50
    },
  
    // Subscription payment for medication delivery service
    subscriptionPayment: {
      id: "pay_sub_003_2024_12_05",
      type: "delivery_subscription",
      status: "paid",
      date: "2024-12-05T00:01:00Z",
      amount: 9.99
    },
  
    // Emergency prescription payment
    emergencyPayment: {
      id: "pay_em_004_2024_12_20",
      type: "emergency_prescription",
      status: "processing",
      date: "2024-12-20T22:45:00Z",
      amount: 95.25
    },
  
    // Bulk medication order
    bulkOrder: {
      id: "pay_bulk_005_2024_11_01",
      type: "bulk_medication_order",
      status: "paid",
      date: "2024-11-01T11:30:00Z",
      amount: 289.99
    }
  };
  
  // Mock data with different payment statuses
  const paymentStatusExamples: MedicationPaymentHistory[] = [
    {
      id: "pay_status_001",
      type: "medication_refill",
      status: "authorized",
      date: "2024-12-21T10:00:00Z",
      amount: 35.00
    },
    {
      id: "pay_status_002",
      type: "prescription_fill",
      status: "declined",
      date: "2024-12-20T14:30:00Z",
      amount: 67.50
    },
    {
      id: "pay_status_003",
      type: "copay",
      status: "disputed",
      date: "2024-12-19T16:15:00Z",
      amount: 25.00
    },
    {
      id: "pay_status_004",
      type: "medication_refill",
      status: "partially_paid",
      date: "2024-12-18T12:45:00Z",
      amount: 50.00
    }
  ];
  
  export { 
    mockMedicalPaymentHistoryResponse, 
    mockPaymentDataVariations, 
    paymentStatusExamples 
  };